/* BJ Beauty — Banner slideshow
   Custom element <bj-banner>. Handles autoplay, dots, arrows, swipe, pause,
   reduced-motion, and Shopify theme-editor block interactions. */

if (!customElements.get('bj-banner')) {
  class BJBanner extends HTMLElement {
    constructor() {
      super();
      this.track = null;
      this.slides = [];
      this.dots = [];
      this.current = 0;
      this.timer = null;
      this.isPlaying = false;
      this.interval = 6000;
      this.loop = true;
      this.touchStart = null;
    }

    connectedCallback() {
      this.track = this.querySelector('.bj-banner__track');
      this.slides = Array.from(this.querySelectorAll('.bj-banner__slide'));
      if (!this.slides.length) return;

      this.dots = Array.from(this.querySelectorAll('.bj-banner__dot'));
      this.interval = parseInt(this.dataset.interval, 10) || 6000;
      this.loop = this.dataset.loop === 'true';
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.waitForVideo = this.dataset.waitForVideo !== 'false';
      // Internal scheduler state
      this._scheduledTimer = null;
      this._endedTarget = null;
      this._endedHandler = null;

      // Detect whether any slide uses the "below" mobile layout — used by CSS for height handling
      const hasBelow = this.slides.some(s => s.classList.contains('bj-banner__slide--mob-below'));
      this.setAttribute('data-mobile-has-below', hasBelow ? 'true' : 'false');

      this.bindEvents();

      // Kick off any videos in the first (active) slide
      this.syncVideos();

      if (this.autoplayEnabled && !this.prefersReducedMotion()) {
        this.play();
      }

      // Pause on tab blur
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.pause(true);
        else if (this.autoplayEnabled && !this.userPaused) this.play();
      });

      // Theme editor: jump to the block the merchant is editing
      document.addEventListener('shopify:block:select', (e) => {
        const idx = this.slides.findIndex(s => s === e.target);
        if (idx > -1) {
          this.userPaused = true;
          this.pause(true);
          this.goTo(idx);
        }
      });

      document.addEventListener('shopify:block:deselect', () => {
        if (this.autoplayEnabled && !this.prefersReducedMotion()) {
          this.userPaused = false;
          this.play();
        }
      });
    }

    disconnectedCallback() {
      this.pause(true);
    }

    bindEvents() {
      // Prev/next
      this.querySelectorAll('.bj-banner__nav').forEach(btn => {
        btn.addEventListener('click', () => {
          this.userPaused = true;
          this.pause(true);
          btn.dataset.direction === 'next' ? this.next() : this.prev();
        });
      });

      // Dots
      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => {
          this.userPaused = true;
          this.pause(true);
          this.goTo(i);
        });
      });

      // Pause/play
      const pauseBtn = this.querySelector('.bj-banner__pause');
      if (pauseBtn) {
        pauseBtn.addEventListener('click', () => {
          if (this.isPlaying) {
            this.userPaused = true;
            this.pause();
            pauseBtn.dataset.state = 'paused';
            pauseBtn.setAttribute('aria-label', 'Play slideshow');
            pauseBtn.querySelector('.bj-banner__pause-icon-pause').hidden = true;
            pauseBtn.querySelector('.bj-banner__pause-icon-play').hidden = false;
          } else {
            this.userPaused = false;
            this.play();
            pauseBtn.dataset.state = 'playing';
            pauseBtn.setAttribute('aria-label', 'Pause slideshow');
            pauseBtn.querySelector('.bj-banner__pause-icon-pause').hidden = false;
            pauseBtn.querySelector('.bj-banner__pause-icon-play').hidden = true;
          }
        });
      }

      // Pause on hover (desktop)
      this.addEventListener('mouseenter', () => {
        if (this.isPlaying) this.pause();
      });
      this.addEventListener('mouseleave', () => {
        if (this.autoplayEnabled && !this.userPaused && !this.prefersReducedMotion()) this.play();
      });

      // Swipe (mobile)
      this.addEventListener('touchstart', (e) => {
        this.touchStart = e.touches[0].clientX;
      }, { passive: true });

      this.addEventListener('touchend', (e) => {
        if (this.touchStart == null) return;
        const delta = e.changedTouches[0].clientX - this.touchStart;
        if (Math.abs(delta) > 50) {
          this.userPaused = true;
          this.pause(true);
          // RTL: invert
          const isRTL = document.documentElement.classList.contains('rtl');
          if ((delta < 0 && !isRTL) || (delta > 0 && isRTL)) this.next();
          else this.prev();
        }
        this.touchStart = null;
      }, { passive: true });

      // Keyboard
      this.addEventListener('keydown', (e) => {
        const isRTL = document.documentElement.classList.contains('rtl');
        if (e.key === 'ArrowLeft')  { this.userPaused = true; this.pause(true); isRTL ? this.next() : this.prev(); }
        if (e.key === 'ArrowRight') { this.userPaused = true; this.pause(true); isRTL ? this.prev() : this.next(); }
      });
    }

    goTo(i) {
      if (i === this.current) return;
      const total = this.slides.length;
      const next = ((i % total) + total) % total;

      this.slides[this.current].setAttribute('data-active', 'false');
      this.slides[this.current].removeAttribute('data-active');
      this.slides[next].setAttribute('data-active', 'true');

      if (this.dots.length) {
        this.dots[this.current]?.classList.remove('is-active');
        this.dots[next]?.classList.add('is-active');
        this.dots.forEach((d, idx) => d.setAttribute('aria-selected', idx === next ? 'true' : 'false'));
      }

      this.current = next;
      this.syncVideos();
    }

    syncVideos() {
      // For every uploaded <video> in the banner, only play the ones on the
      // currently active slide. Pause and rewind everything else so they don't
      // burn CPU or audio in the background.
      const singleSlide = this.slides.length < 2;
      const allVideos = this.querySelectorAll('video[data-bj-video]');
      allVideos.forEach(v => {
        const slide = v.closest('.bj-banner__slide');
        const isActive = slide && slide.getAttribute('data-active') === 'true';
        // Loop only when single slide OR autoplay is OFF OR wait-for-video is disabled.
        // In multi-slide "wait for video" mode the video must end, so we cannot loop.
        v.loop = singleSlide || !this.autoplayEnabled || !this.waitForVideo;
        if (isActive) {
          // Guarantee the muted/playsinline state every time (Safari sometimes drops it)
          v.muted = true;
          v.playsInline = true;
          const playAttempt = v.play();
          if (playAttempt && typeof playAttempt.catch === 'function') {
            playAttempt.catch(() => { /* autoplay blocked — poster will show */ });
          }
        } else {
          try { v.pause(); v.currentTime = 0; } catch (e) { /* noop */ }
        }
      });
    }

    /* Pick the currently-visible <video> on the active slide.
       Returns null if the slide has no video or only an embed. */
    getActiveVideo() {
      const slide = this.slides[this.current];
      if (!slide) return null;
      if (slide.getAttribute('data-has-video') !== 'true') return null;
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      const container = isMobile
        ? slide.querySelector('.bj-banner__media--mobile')
        : slide.querySelector('.bj-banner__media--desktop');
      return container ? container.querySelector('video[data-bj-video]') : null;
    }

    next() {
      const total = this.slides.length;
      if (!this.loop && this.current === total - 1) {
        // No more slides — stop autoplay
        this.isPlaying = false;
        return;
      }
      this.goTo((this.current + 1) % total);
      if (this.isPlaying) this.scheduleNext();
    }

    prev() {
      const total = this.slides.length;
      if (!this.loop && this.current === 0) return;
      this.goTo((this.current - 1 + total) % total);
      if (this.isPlaying) this.scheduleNext();
    }

    play() {
      if (this.slides.length < 2) return;
      this.clearScheduled();
      this.isPlaying = true;
      this.scheduleNext();
    }

    /* Schedule the next slide advance — either when the active video ends,
       or after the fixed interval if there's no playable video. */
    scheduleNext() {
      this.clearScheduled();
      if (!this.isPlaying) return;

      const video = this.waitForVideo ? this.getActiveVideo() : null;

      if (video) {
        const advance = () => {
          this.clearScheduled();
          if (this.isPlaying) this.next();
        };
        this._endedTarget = video;
        this._endedHandler = advance;
        video.addEventListener('ended', advance);

        // Safety net: if 'ended' doesn't fire (e.g., autoplay blocked, network stall),
        // fall back to either the known duration + slack, or the configured interval.
        const armFallback = () => {
          const d = video.duration;
          const ms = (!isNaN(d) && d > 0) ? (d * 1000) + 1500 : this.interval;
          this._scheduledTimer = setTimeout(advance, ms);
        };
        if (video.readyState >= 1 && !isNaN(video.duration) && video.duration > 0) {
          armFallback();
        } else {
          video.addEventListener('loadedmetadata', armFallback, { once: true });
          // Also arm an initial interval fallback in case metadata never loads
          this._scheduledTimer = setTimeout(advance, this.interval * 2);
        }
      } else {
        this._scheduledTimer = setTimeout(() => {
          this.clearScheduled();
          if (this.isPlaying) this.next();
        }, this.interval);
      }
    }

    clearScheduled() {
      if (this._scheduledTimer) {
        clearTimeout(this._scheduledTimer);
        this._scheduledTimer = null;
      }
      if (this._endedTarget && this._endedHandler) {
        try { this._endedTarget.removeEventListener('ended', this._endedHandler); } catch (e) {}
      }
      this._endedTarget = null;
      this._endedHandler = null;
    }

    pause(hardStop = false) {
      this.clearScheduled();
      this.isPlaying = false;
    }

    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
  }

  customElements.define('bj-banner', BJBanner);
}